import { useEffect, useRef, useState } from 'preact/hooks';
import {
  messagesSignal,
  hasMoreLocalMessagesSignal,
  isLoadingOlderMessagesSignal,
  loadMoreLocalMessages
} from '../../state/messages.store';
import { activeContactSignal } from '../../state/contacts.store';
import { activeTabSignal, highlightedMessageIdSignal } from '../../state/ui.store';
import { MessageList } from './MessageList';
import { ArrowDown } from 'lucide-preact';

interface ChatContainerProps {
  onOpenVisionModal?: (mediaUrl: string, content: string, messageId?: string) => void;
}

export function ChatContainer({ onOpenVisionModal }: ChatContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const messages = messagesSignal.value;
  const activeContact = activeContactSignal.value;
  const activeTab = activeTabSignal.value;
  const highlightedId = highlightedMessageIdSignal.value;
  const prevMessagesLengthRef = useRef<number>(0);
  const currentPhoneRef = useRef<string | null>(null);
  const isSwitchedRecentlyRef = useRef<boolean>(false);
  const switchTimeoutRef = useRef<any>(null);
  const [showScrollBottom, setShowScrollBottom] = useState<boolean>(false);

  const isGroup = Boolean(activeContact?.is_group || (activeContact?.jid && activeContact.jid.includes('@g.us')));

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth'
      });
      setShowScrollBottom(false);
    }
  };

  const forceScrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      setShowScrollBottom(false);
    }
  };

  // ResizeObserver para re-ancorar no final automaticamente quando mídias, áudios e imagens carregam e expandem a altura da lista
  useEffect(() => {
    if (!contentRef.current || !containerRef.current) return;

    const observer = new ResizeObserver(() => {
      if (!containerRef.current) return;
      const el = containerRef.current;
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 250;

      if ((isSwitchedRecentlyRef.current || isNearBottom) && !highlightedMessageIdSignal.value) {
        forceScrollToBottom();
      }
    });

    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!containerRef.current || !activeContact) return;
    const el = containerRef.current;

    const markSwitchedRecently = () => {
      isSwitchedRecentlyRef.current = true;
      if (switchTimeoutRef.current) clearTimeout(switchTimeoutRef.current);
      switchTimeoutRef.current = setTimeout(() => {
        isSwitchedRecentlyRef.current = false;
      }, 3000);
    };

    const performScrollToBottom = () => {
      forceScrollToBottom();
      requestAnimationFrame(forceScrollToBottom);
      setTimeout(forceScrollToBottom, 50);
      setTimeout(forceScrollToBottom, 150);
      setTimeout(forceScrollToBottom, 350);
    };

    // Se houver um ID destacado, evitar auto-scroll para o final
    if (highlightedMessageIdSignal.value) return;

    // 1. Alternou de contato -> Rolar imediatamente para o final e ativar janela de re-ancoragem de 3s
    if (currentPhoneRef.current !== activeContact.phone_number) {
      currentPhoneRef.current = activeContact.phone_number;
      prevMessagesLengthRef.current = messages.length;
      markSwitchedRecently();
      performScrollToBottom();
      return;
    }

    // 2. Mensagens carregaram do banco/cache (de 0 para N) -> Rolar para o final
    if (prevMessagesLengthRef.current === 0 && messages.length > 0) {
      prevMessagesLengthRef.current = messages.length;
      markSwitchedRecently();
      performScrollToBottom();
      return;
    }

    // 3. Nova mensagem recebida/enviada no final
    if (messages.length > prevMessagesLengthRef.current) {
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 250;
      if (isNearBottom) {
        performScrollToBottom();
      } else {
        setShowScrollBottom(true);
      }
      prevMessagesLengthRef.current = messages.length;
    }
  }, [messages.length, activeContact?.phone_number, activeTab, highlightedId]);

  // Efeito dedicado ao alternar de aba de volta para 'tab-messages'
  useEffect(() => {
    if (activeTab === 'tab-messages' && containerRef.current && activeContact) {
      if (highlightedMessageIdSignal.value) return;
      isSwitchedRecentlyRef.current = true;
      forceScrollToBottom();
      setTimeout(forceScrollToBottom, 100);
      setTimeout(forceScrollToBottom, 300);
      setTimeout(() => {
        isSwitchedRecentlyRef.current = false;
      }, 2000);
    }
  }, [activeTab]);

  // Efeito dedicado para scroll suave e foco na mensagem de origem (highlightedMessageIdSignal)
  useEffect(() => {
    const targetId = highlightedMessageIdSignal.value;
    if (!targetId || activeTab !== 'tab-messages') return;

    const attemptScroll = () => {
      const el = document.getElementById(`msg-${targetId}`);
      if (el && containerRef.current) {
        isSwitchedRecentlyRef.current = false;
        const container = containerRef.current;
        const targetTop = el.offsetTop - container.offsetTop - (container.clientHeight / 2) + (el.clientHeight / 2);
        container.scrollTo({
          top: Math.max(0, targetTop),
          behavior: 'smooth'
        });
        return true;
      }
      return false;
    };

    if (!attemptScroll()) {
      const timer1 = setTimeout(attemptScroll, 100);
      const timer2 = setTimeout(attemptScroll, 250);
      const timer3 = setTimeout(attemptScroll, 500);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }

    const clearTimer = setTimeout(() => {
      if (highlightedMessageIdSignal.value === targetId) {
        highlightedMessageIdSignal.value = null;
      }
    }, 4000);

    return () => clearTimeout(clearTimer);
  }, [highlightedId, activeTab, messages]);

  // PageUp / PageDown / Home / End keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!containerRef.current || !activeContactSignal.value) return;

      const target = e.target as HTMLElement | null;
      const isInput = target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      );

      const keysToIntercept = ['PageUp', 'PageDown', 'Home', 'End'];
      if (!keysToIntercept.includes(e.key)) return;

      const el = containerRef.current;
      const pageStep = Math.max(200, el.clientHeight * 0.8);

      if (e.key === 'PageUp') {
        e.preventDefault();
        el.scrollBy({ top: -pageStep, behavior: 'smooth' });
      } else if (e.key === 'PageDown') {
        e.preventDefault();
        el.scrollBy({ top: pageStep, behavior: 'smooth' });
      } else if (e.key === 'Home' && !isInput) {
        e.preventDefault();
        el.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (e.key === 'End' && !isInput) {
        e.preventDefault();
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      }

      setTimeout(() => {
        if (containerRef.current) {
          const dist = containerRef.current.scrollHeight - containerRef.current.scrollTop - containerRef.current.clientHeight;
          setShowScrollBottom(dist > 200);
        }
      }, 100);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeContact?.phone_number]);

  const handleScroll = async () => {
    if (!containerRef.current || !activeContact) return;
    const el = containerRef.current;

    // Check distance to bottom to toggle floating button
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollBottom(distanceToBottom > 200);

    // Infinite scroll for older messages when reaching top
    if (el.scrollTop <= 30 && hasMoreLocalMessagesSignal.value && !isLoadingOlderMessagesSignal.value) {
      const oldScrollHeight = el.scrollHeight;
      const oldScrollTop = el.scrollTop;

      const loadedCount = await loadMoreLocalMessages(activeContact.phone_number);
      if (loadedCount > 0 && containerRef.current) {
        const newScrollHeight = containerRef.current.scrollHeight;
        containerRef.current.scrollTop = newScrollHeight - oldScrollHeight + oldScrollTop;
      }
    }
  };

  return (
    <div class="relative flex-1 flex flex-col min-h-0 overflow-hidden">
      <div
        ref={containerRef}
        id="tab-messages"
        tabIndex={0}
        onScroll={handleScroll}
        class="tab-content flex-1 overflow-y-auto overflow-x-hidden p-2 pr-[max(0.75rem,env(safe-area-inset-right))] sm:pr-4 focus:outline-none"
      >
        <div ref={contentRef}>
          <MessageList
            messages={messages}
            isGroup={isGroup}
            onOpenVisionModal={onOpenVisionModal}
          />
        </div>
      </div>

      {showScrollBottom && (
        <button
          onClick={scrollToBottom}
          title="Rolar para a última mensagem"
          class="absolute bottom-4 right-10 sm:right-12 z-20 flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-cyan-600/90 hover:bg-cyan-500 text-white shadow-xl border border-cyan-400/50 backdrop-blur-md transition-[transform,background-color] duration-200 transform hover:scale-105 active:scale-95 text-xs font-bold cursor-pointer"
        >
          <ArrowDown class="w-4 h-4 text-white animate-bounce shrink-0" />
          <span>Últimas mensagens</span>
        </button>
      )}
    </div>
  );
}
